import React, {useMemo, useState, useEffect} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {Link, useNavigate} from 'react-router-dom';

import {DataGrid, KeenIcon} from '@/components';
import {Loading} from '@/layouts/buddy/loading';
import {DeleteCompanyConfirmationModal} from '@/pages/buddy/company/blocks/delete-company/DeleteCompanyModal';
import {CompanyDetailsData} from '@/pages/buddy/types/CompanyDetailsData';
import {formatDate} from "@/pages/buddy/utils/Date.tsx";

interface CompaniesProps {
    companies: CompanyDetailsData[];
    loading: boolean;
    on_delete: (companyId: string) => void; // Add the on_delete prop
}

const Companies: React.FC<CompaniesProps> = ({companies, loading, on_delete}) => {
    const storageFilterId = 'companies-filter';
    const [isActiveFilter, setIsActiveFilter] = useState(true);

    const navigate = useNavigate();

    // Search term state
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem(storageFilterId) || '';
    });

    // Save search term to local storage
    useEffect(() => {
        localStorage.setItem(storageFilterId, searchTerm);
    }, [searchTerm]);

    // Filtered data based on search term and active status
    const filteredData = useMemo(() => {
        return companies.filter((company) => {
            const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesActiveFilter = !isActiveFilter || company.is_active;
            return matchesSearch && matchesActiveFilter;
        });
    }, [companies, searchTerm, isActiveFilter]);

    // Modal state for delete confirmation
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<CompanyDetailsData | null>(null);

    const handleDeleteClick = (company: CompanyDetailsData) => {
        setSelectedCompany(company);
        setModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (selectedCompany) {
            on_delete(selectedCompany.p_id); // Call the on_delete function passed as prop
        }
        setModalOpen(false);
    };

    const columns = useMemo<ColumnDef<CompanyDetailsData>[]>(() => [
        {
            accessorFn: (row) => row.name,
            id: 'name',
            header: () => 'Company',
            enableSorting: true,
            cell: (info) => (
                <div className="flex flex-col">
                    <Link
                        className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
                        to={`/company/${info.row.original.p_id}`}
                    >
                        {info.row.original.name}
                    </Link>
                </div>
            ),
        },
        {
            id: 'view-jobs',
            header: () => 'View Jobs',
            enableSorting: false,
            cell: ({row}) => (
                <div className="flex text-sm font-normal items-center gap-2">
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => navigate(`/company/${row.original.p_id}/jobs`)}
                    >
                        <KeenIcon icon="eye"/>
                    </button>
                    <Link
                        className="leading-none hover:text-primary"
                        to={`/company/${row.original.p_id}/jobs`}
                    >
                        {row.original.jobs ? row.original.jobs.length : 0} Jobs
                    </Link>
                </div>
            ),
        },
        {
            accessorFn: (row) => row.updated_at,
            id: 'lastModified',
            enableSorting: true,
            header: () => 'Last Modified',
            cell: (info) => {
                const value = info.getValue() as string | null; // Explicitly cast the value
                return formatDate(value);
            },
            meta: {
                className: 'w-[350px]',
                cellClassName: 'text-gray-700 font-normal',
            },
        },
        {
            id: 'edit',
            header: () => 'Open',
            enableSorting: false,
            cell: ({row}) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => navigate(`/company/${row.original.p_id}`)}
                >
                    <KeenIcon icon="notepad-edit"/>
                </button>
            ),
            meta: {
                className: 'w-[60px]',
            },
        },
        {
            id: 'delete',
            header: () => 'Delete',
            enableSorting: false,
            cell: ({row}) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => handleDeleteClick(row.original)}
                >
                    <KeenIcon icon="trash"/>
                </button>
            ),
            meta: {
                className: 'w-[60px]',
            },
        },
    ], [navigate]);

    if (loading) return <Loading loading={loading}/>;

    return (
        <>
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-5">
                    <h3 className="card-title">Companies</h3>
                    <div className="flex gap-6">
                        <div className="relative">
                            <KeenIcon
                                icon="magnifier"
                                className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                            />
                            <input
                                type="text"
                                placeholder="Search Companies"
                                className="input input-sm pl-8"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <label className="switch switch-sm">
                            <input
                                name="check"
                                type="checkbox"
                                checked={isActiveFilter}
                                onChange={() => setIsActiveFilter(!isActiveFilter)}
                                className="order-2"
                            />
                            <span className="switch-label order-1">Only Active Companies</span>
                        </label>
                    </div>
                </div>
                <div className="card-body">
                    <DataGrid
                        key={`${searchTerm}-${isActiveFilter}`} // React to search term and filter changes
                        columns={columns}
                        data={filteredData}
                        pagination={{size: 10}}
                        sorting={[{id: 'name', desc: false}]}
                    />
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {selectedCompany && (
                <DeleteCompanyConfirmationModal
                    open={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                    companyName={selectedCompany?.name || ''}
                />
            )}
        </>
    );
};

export {Companies};
